import React, { useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useCreateAdmin, useUpdateAdmin } from '../../../services/users';
import { useForm } from '../../../hooks/useForm';
import TimezoneSelect from '../../common/TimezoneSelect';
import AddressInput from '../../common/AddressInput';

const DEFAULTS = {
  userEmail: '',
  userFirstName: '',
  userLastName: '',
  userDateOfBirth: '1950-01-01',
  userTimezone: 'America/Chicago',
  internalUserPhone: '',
  userRcExtension: '',
  userRcExtensionId: '',
  userState: '',
  userType: 'director',
  referralCommissionRate: 10,
  userAssignable: true,
  addressLine1: '',
  addressLine2: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  addressCountry: 'US',
  latitude: null,
  longitude: null,
};

const AdminModal = ({ show, toggle, user, onSuccess }) => {
  const { form, onChange, setValues } = useForm(DEFAULTS);

  useEffect(() => {
    if (user) {
      setValues({ ...DEFAULTS, ...user });
    } else {
      setValues(DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const sendInvite = useCreateAdmin(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }

      toast.success('Admin created and invite sent successfully.');
      toggle();
    },
    { onError: (err) => toast.error(err.message) },
  );

  const updateAdmin = useUpdateAdmin(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }

      toast.success('Admin successfully updated.');
      toggle();
    },
    { onError: (err) => toast.error(err.message) },
  );

  const onSubmit = (e) => {
    e.preventDefault();

    const payload = {
      userEmail: form.userEmail,
      userFirstName: form.userFirstName,
      userLastName: form.userLastName,
      userTimezone: form.userTimezone,
      // Mirror the structured address state + city onto the legacy
      // userState / userCity columns so any consumer still reading them
      // stays in sync (CSV export, dbqPeopleController, getProviderProfile,
      // etc.).
      userCity: form.addressCity || form.userCity || null,
      userState: form.addressState || form.userState || null,
      userType: form.userType,
      internalUserPhone: form.internalUserPhone?.trim() || null,
      userRcExtension: form.userRcExtension?.trim() || null,
      referralCommissionRate: form.referralCommissionRate,
      userAssignable: form.userAssignable !== false,
      addressLine1: form.addressLine1 || null,
      addressLine2: form.addressLine2 || null,
      addressCity: form.addressCity || null,
      addressState: form.addressState || null,
      addressZip: form.addressZip || null,
      addressCountry: form.addressCountry || 'US',
      latitude: form.latitude ?? null,
      longitude: form.longitude ?? null,
    };

    // redirectReferralsTo is deliberately NOT sent: the Referral Partner type
    // is retired and the server preserves any legacy value when the key is
    // absent.

    if (form.userId) {
      updateAdmin.mutate({
        userId: form.userId,
        payload,
      });
    } else {
      sendInvite.mutate(payload);
    }
  };

  return (
    <Modal show={show} onHide={() => toggle()}>
      <Modal.Body>
        <h3>{user ? 'Edit User' : 'Create User'}</h3>
        {user && user.userId ? (
          <p>
            <strong>Referral Link:</strong>
            <br />
            {window.location.protocol}\/\/{window.location.host}/referral/
            {user.userId}
          </p>
        ) : null}
        <form id="invite-form" onSubmit={onSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="userEmail"
              onChange={onChange}
              value={form.userEmail}
              required
              placeholder="user@email.com"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              name="userFirstName"
              onChange={onChange}
              value={form.userFirstName}
              required
              placeholder="First"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              name="userLastName"
              onChange={onChange}
              value={form.userLastName}
              required
              placeholder="Last"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select
              name="userType"
              onChange={onChange}
              value={form.userType}
            >
              <option value="admin">Administrator</option>
              <option value="claims_admin">Claims Administrator</option>
              <option value="billing_admin">Billing Admin</option>
              <option value="director">Director</option>
              <option value="claims_manager">Claims Manager</option>
              <option value="onboarding_manager">Onboarding Manager</option>
              <option value="onboarding_specialist">
                Onboarding Specialist
              </option>
              <option value="onboarding_support_specialist">Sales Rep</option>
              {/* Retired type: only rendered (disabled) so a legacy referral
                  user's row still displays correctly when edited. New
                  referrers belong on the Partners page. */}
              {form.userType === 'referral' ? (
                <option value="referral" disabled>
                  Referral Partner (legacy)
                </option>
              ) : null}
              <option value="it_support">IT Support</option>
            </Form.Select>
          </Form.Group>
          <AddressInput
            value={{
              line1: form.addressLine1 || '',
              line2: form.addressLine2 || '',
              city: form.addressCity || '',
              state: form.addressState || '',
              zip: form.addressZip || '',
              country: form.addressCountry || 'US',
              latitude: form.latitude ?? null,
              longitude: form.longitude ?? null,
            }}
            onChange={(next) =>
              setValues({
                ...form,
                addressLine1: next.line1 || '',
                addressLine2: next.line2 || '',
                addressCity: next.city || '',
                addressState: next.state || '',
                addressZip: next.zip || '',
                addressCountry: next.country || 'US',
                latitude: next.latitude ?? null,
                longitude: next.longitude ?? null,
              })
            }
            label="Address"
            placeholder="Start typing the user's street address..."
            idPrefix="admin-address"
          />
          <Form.Group className="mb-3">
            <Form.Label>Timezone</Form.Label>
            <TimezoneSelect
              name="userTimezone"
              onChange={onChange}
              value={form.userTimezone}
              require={true}
            />
          </Form.Group>
          {/* RingCentral identity: everything phone-related in one place,
              with the derived SMS ID VISIBLE (read-only) so "is this person
              good to go?" is never a guessing game (Nic 2026-08-10). */}
          <hr />
          <h5 className="mb-1">RingCentral</h5>
          <p className="mb-3 text-muted" style={{ fontSize: '0.875em' }}>
            Filled by Sync from RingCentral, or type the values and save.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Direct Phone</Form.Label>
            <Form.Control
              name="internalUserPhone"
              onChange={onChange}
              value={form.internalUserPhone || ''}
              placeholder="(615) 555-0100"
            />
            <Form.Text muted>
              Their direct line: the caller ID on their board calls and the
              number their texts come from. Also shown on claims they are
              assigned to.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Extension</Form.Label>
            <Form.Control
              name="userRcExtension"
              onChange={onChange}
              value={form.userRcExtension || ''}
              placeholder="101"
            />
            <Form.Text muted>
              Their extension number: the live-call transfer target. Must exist
              in RingCentral.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>SMS ID</Form.Label>
            <Form.Control
              value={form.userRcExtensionId || ''}
              disabled
              placeholder="Not linked"
            />
            {form.userRcExtensionId ? (
              <Form.Text className="text-success">
                Linked to RingCentral. Texting from their own line is good to
                go.
              </Form.Text>
            ) : form.userRcExtension ? (
              <Form.Text className="text-warning">
                Not linked yet. Save to look up extension {form.userRcExtension}{' '}
                in RingCentral; texting from their line stays blocked until this
                fills. Reopen this window after saving to check.
              </Form.Text>
            ) : (
              <Form.Text muted>
                Fills automatically once an extension is saved. Texting from
                their line needs it.
              </Form.Text>
            )}
          </Form.Group>
          <hr />
          <Form.Group className="mb-3">
            <Form.Label>Commission Rate</Form.Label>
            <Form.Control
              name="referralCommissionRate"
              onChange={onChange}
              value={form.referralCommissionRate}
              required
              type="number"
              placeholder="10"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="user-assignable-switch"
              label="Selectable in assignment dropdowns"
              checked={form.userAssignable !== false}
              onChange={(e) =>
                onChange({
                  target: { name: 'userAssignable', value: e.target.checked },
                })
              }
            />
            <Form.Text muted>
              Off = this person never appears as a pick in claim, lead, or
              partner routing dropdowns and gets no assignment-driven email.
            </Form.Text>
          </Form.Group>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          variant="success"
          form="invite-form"
          disabled={sendInvite.isLoading}
        >
          <FontAwesomeIcon
            icon={sendInvite.isLoading ? faSpinner : faCheck}
            spin={sendInvite.isLoading}
          />{' '}
          Save
        </Button>{' '}
        <Button variant="link" onClick={() => toggle()}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminModal;
